// Segment-aware helpers over the case category list returned by
// GET /category/subcategory ([{ segment, type, category, subcategory }]).
//
// Every transaction subcategory belongs to one ledger segment — PERSONAL,
// BUSINESS or INVESTMENT (.claude/references/categorization-segments/README.md).
// A subcategory NAME is only unique within (segment, type), so every lookup,
// option key and duplicate check here carries the segment. Rows without a
// segment (a case not yet migrated to 1.6.8) are PERSONAL.

export const DEFAULT_SEGMENT = 'PERSONAL';

export const SEGMENT_LABELS = {
  PERSONAL: 'Personal',
  BUSINESS: 'Business',
  INVESTMENT: 'Investment',
};

const SEGMENT_ORDER = ['PERSONAL', 'BUSINESS', 'INVESTMENT'];

export const segmentOf = (item) => (item && item.segment) || DEFAULT_SEGMENT;

export const segmentLabel = (segment) => SEGMENT_LABELS[segment] || segment;

const uniqueSorted = (values) =>
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

/** Distinct segments present in the category list, in chart order. */
export const getSegments = (categoryList) => {
  const present = new Set((categoryList || []).map(segmentOf));
  return SEGMENT_ORDER.filter((s) => present.has(s))
    .concat([...present].filter((s) => !SEGMENT_ORDER.includes(s)).sort());
};

export const getTypes = (categoryList, segment) =>
  uniqueSorted((categoryList || []).filter((i) => segmentOf(i) === segment).map((i) => i.type));

export const getCategories = (categoryList, segment, type) => {
  if (!type) return [];
  return uniqueSorted((categoryList || [])
    .filter((i) => segmentOf(i) === segment && i.type === type)
    .map((i) => i.category));
};

export const getSubcategories = (categoryList, segment, type, category) => {
  if (!type || !category) return [];
  return uniqueSorted((categoryList || [])
    .filter((i) => segmentOf(i) === segment && i.type === type && i.category === category)
    .map((i) => i.subcategory));
};

/**
 * An existing entry that a new (segment, type, category, subcategory) choice
 * would collide with, or null. Mirrors the backend rule
 * (category.ensure_subcategory_exists): within a segment a subcategory name may
 * live under only one (type, category). The same name in another segment is a
 * different subcategory and is fine.
 */
export const findSubcategoryConflict = (categoryList, { segment, type, category, subcategory }) => {
  const name = (subcategory || '').trim().toLowerCase();
  if (!name || !type || !category || !categoryList) return null;
  return categoryList.find((i) =>
    segmentOf(i) === segment &&
    i.subcategory &&
    i.subcategory.toLowerCase() === name &&
    (i.type !== type || i.category !== category)) || null;
};

/** Default segment for an edit: the transaction's current subcategory
 *  segment, else the account's ledger segment, else PERSONAL — but only a
 *  segment the case actually has a chart for (`availableSegments`, when
 *  known). An account can derive to BUSINESS before a BUSINESS chart exists;
 *  defaulting to it would leave every dropdown empty. */
export const resolveInitialSegment = (currentSegment, accountSegment, availableSegments) => {
  const preferred = [currentSegment, accountSegment, DEFAULT_SEGMENT].filter(Boolean);
  if (!availableSegments || availableSegments.length === 0) return preferred[0];
  return preferred.find((s) => availableSegments.includes(s)) || availableSegments[0];
};

/** Stable identity of a subcategory option across segments and types. */
export const subcategoryOptionKey = (option) =>
  option ? `${segmentOf(option)}|${option.type || ''}|${option.subcategory || ''}` : '';

/** Option label; a name that appears more than once in `options` is
 *  disambiguated with its type and (when not PERSONAL) its segment. */
export const subcategoryOptionLabel = (option, options) => {
  if (!option) return '';
  const name = option.subcategory || '';
  const dupes = (options || []).filter((o) => o.subcategory === name).length;
  if (dupes <= 1) return name;
  const segment = segmentOf(option);
  const suffix = segment === DEFAULT_SEGMENT ? option.type : `${option.type}, ${segmentLabel(segment)}`;
  return `${name} (${suffix})`;
};

// Category types that are NOT cash flow (categorization-segments ADR-001
// Decision B): Position = an investment buy, sell or sweep inside the account;
// Valuation = a market-value change. Never income or spending, so every
// inflow/outflow view leaves them out.
export const NON_CASH_TYPES = ['Position', 'Valuation'];

/** True for an enriched transaction row whose category is non-cash. */
export const isNonCashTransaction = (txn) => NON_CASH_TYPES.includes(txn?.subcategory?.type);
